import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllCertificates, updateCertificate, deleteCertificate,
  uploadCertificateFile, deleteCertificateFile,
  getClients, getTasks,
  revealCertificateArchiveLink, uploadsUrl,
} from '../api';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import ColumnFilter, { FilterOption } from '../components/ColumnFilter';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

interface CertInvoice { id: number; invoiceNumber: string; status: string; totalAmount: any; }
interface CertTask { id: number; taskId: string; taskName: string; managerId?: number | null; partnerId?: number | null; client: { id: number; clientName: string }; }
interface Certificate {
  id: number;
  certificateNumber: string;
  taskId: number;
  task: CertTask;
  name: string;
  description?: string;
  assignedTo?: { id: number; staffName: string } | null;
  dueDate?: string;
  udin?: string;
  udinDate?: string;
  archiveLink?: string;
  archiveLinkSet?: boolean;
  fileName?: string;
  originalName?: string;
  fileSize?: number;
  mimeType?: string;
  invoice?: CertInvoice;
  createdAt: string;
}

type EditForm = {
  name: string; description: string; assignedToId: string; dueDate: string;
  udin: string; udinDate: string; archiveLink: string;
};

type InlineField = 'udin' | 'udinDate' | 'archiveLink';

const CertStatusDot: React.FC<{ closed: boolean; invoiced: boolean }> = ({ closed, invoiced }) => (
  <div className="flex items-center gap-1.5" title={`${closed ? 'Closed' : 'Open'} · ${invoiced ? 'Invoiced' : 'Not invoiced'}`}>
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${closed ? 'bg-gray-400' : 'bg-green-500'}`} title={closed ? 'Closed' : 'Open'} />
    <span className={`inline-block w-2.5 h-2.5 rounded-full border-2 ${invoiced ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`} title={invoiced ? 'Invoiced' : 'Not invoiced'} />
  </div>
);

const fmtSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (s?: string) => (s ? s.slice(0, 10) : '—');

const isClosed = (cert: Certificate) =>
  !!(cert.udin && cert.udinDate && (cert.archiveLinkSet || cert.archiveLink) && cert.fileName);

const Certificates: React.FC = () => {
  const { isAdmin, isHR, isPartner, user } = useAuth();
  const navigate = useNavigate();
  const canEditCert = (cert: Certificate) =>
    isAdmin || isHR || isPartner ||
    (user?.staffId != null && (
      cert.task.managerId === user.staffId ||
      cert.task.partnerId === user.staffId ||
      cert.assignedTo?.id === user.staffId
    ));

  const [certs, setCerts] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterTaskId, setFilterTaskId] = useState('');
  const [filterStatus, setFilterStatus] = useState<'open' | 'closed' | 'all'>('open');
  const [filterBilling, setFilterBilling] = useState<'all' | 'billed' | 'unbilled'>('all');
  const [clients, setClients] = useState<{ id: number; clientName: string }[]>([]);
  const [tasks, setTasks] = useState<{ id: number; taskId: string; taskName: string }[]>([]);

  // Full edit mode (name / description / assignedTo / dueDate)
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    name: '', description: '', assignedToId: '', dueDate: '',
    udin: '', udinDate: '', archiveLink: '',
  });
  const [staff, setStaff] = useState<{ id: number; staffName: string; isActive?: boolean }[]>([]);
  const [saving, setSaving] = useState(false);

  // Inline edit (UDIN / UDIN Date / Archive Link)
  const [inlineEdit, setInlineEdit] = useState<{ certId: number; field: InlineField; value: string } | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);

  // File upload
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  // Bulk selection (for multi-cert invoice)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Sorting
  const [sortField, setSortField] = useState<'certificateNumber' | 'task' | 'client' | 'name' | 'assignedTo' | 'createdAt' | 'dueDate' | 'udin' | 'udinDate' | 'status'>('certificateNumber');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (f: typeof sortField) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };
  const SI = ({ f }: { f: typeof sortField }) =>
    sortField !== f ? <span className="ml-1 text-gray-300">↕</span>
    : <span className="ml-1 text-blue-600">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (search) params.search = search;
      if (filterClientId) params.clientId = filterClientId;
      if (filterTaskId) params.taskId = filterTaskId;
      const res = await getAllCertificates(params);
      setCerts(res.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [search, filterClientId, filterTaskId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin || isHR || isPartner) {
      getClients().then((r) => setClients(r.data)).catch(() => {});
      getTasks().then((r) => setTasks(r.data)).catch(() => {});
      import('../api').then(({ getStaff }) => getStaff().then((r) => setStaff(r.data)).catch(() => {}));
    }
  }, [isAdmin, isHR, isPartner]);

  const displayedCerts = useMemo(() => {
    let list = certs;
    if (filterStatus === 'closed') list = list.filter(isClosed);
    else if (filterStatus === 'open') list = list.filter((c) => !isClosed(c));
    if (filterBilling === 'billed') list = list.filter((c) => !!c.invoice);
    else if (filterBilling === 'unbilled') list = list.filter((c) => !c.invoice);
    return [...list].sort((a, b) => {
      let av = '', bv = '';
      if (sortField === 'certificateNumber') { av = a.certificateNumber || ''; bv = b.certificateNumber || ''; }
      else if (sortField === 'task') { av = a.task?.taskId || ''; bv = b.task?.taskId || ''; }
      else if (sortField === 'client') { av = a.task?.client?.clientName || ''; bv = b.task?.client?.clientName || ''; }
      else if (sortField === 'name') { av = a.name || ''; bv = b.name || ''; }
      else if (sortField === 'assignedTo') { av = a.assignedTo?.staffName || ''; bv = b.assignedTo?.staffName || ''; }
      else if (sortField === 'createdAt') { av = a.createdAt || ''; bv = b.createdAt || ''; }
      else if (sortField === 'dueDate') { av = a.dueDate || ''; bv = b.dueDate || ''; }
      else if (sortField === 'udin') { av = a.udin || ''; bv = b.udin || ''; }
      else if (sortField === 'udinDate') { av = a.udinDate || ''; bv = b.udinDate || ''; }
      else if (sortField === 'status') { av = isClosed(a) ? 'closed' : 'open'; bv = isClosed(b) ? 'closed' : 'open'; }
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [certs, filterStatus, filterBilling, sortField, sortDir]);

  // ── Bulk selection helpers ─────────────────────────────────────────────────
  // Only unbilled certificates can be selected for multi-cert invoice creation.
  const selectableCerts = useMemo(() => displayedCerts.filter((c) => !c.invoice), [displayedCerts]);
  const selectedList = useMemo(() => certs.filter((c) => selectedIds.has(c.id)), [certs, selectedIds]);
  const selectedClientIds = useMemo(
    () => Array.from(new Set(selectedList.map((c) => c.task?.client?.id).filter(Boolean))),
    [selectedList]
  );
  const bulkClientLocked = selectedClientIds.length === 1 ? selectedClientIds[0] : null;
  const bulkClientMixed = selectedClientIds.length > 1;

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const allSelectable = selectableCerts.map((c) => c.id);
    const allSelected = allSelectable.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of allSelectable) next.delete(id);
      } else {
        for (const id of allSelectable) next.add(id);
      }
      return next;
    });
  };

  const createMultiCertInvoice = () => {
    if (selectedIds.size < 2) {
      alert('Select at least 2 unbilled certificates to create a multi-certificate invoice.');
      return;
    }
    if (bulkClientMixed) {
      alert('All selected certificates must belong to the same client.');
      return;
    }
    const ids = Array.from(selectedIds).join(',');
    navigate(`/invoices?certificateIds=${ids}`);
  };

  // ── Full edit ──────────────────────────────────────────────────────────────

  const startEdit = (cert: Certificate) => {
    setEditingId(cert.id);
    setInlineEdit(null);
    setEditForm({
      name: cert.name,
      description: cert.description || '',
      assignedToId: cert.assignedTo ? String(cert.assignedTo.id) : '',
      dueDate: cert.dueDate ? cert.dueDate.slice(0, 10) : '',
      udin: cert.udin || '',
      udinDate: cert.udinDate ? cert.udinDate.slice(0, 10) : '',
      archiveLink: '', // populated asynchronously via reveal below
    });
    // Lazy-fetch the decrypted archive link only when the editor opens —
    // audited server-side. If reveal fails the user just types a fresh value.
    if (cert.archiveLinkSet || cert.archiveLink) {
      revealCertificateArchiveLink(cert.id)
        .then((res) => setEditForm((prev) => ({ ...prev, archiveLink: res.data?.url || '' })))
        .catch(() => { /* leave blank */ });
    }
  };

  const cancelEdit = () => { setEditingId(null); };

  const saveEdit = async (cert: Certificate) => {
    setSaving(true);
    try {
      const payload: any = {
        name: editForm.name,
        description: editForm.description || null,
        assignedToId: editForm.assignedToId || null,
        dueDate: editForm.dueDate || null,
        udin: editForm.udin || null,
        udinDate: editForm.udinDate || null,
        archiveLink: editForm.archiveLink || null,
      };
      // Race guard: if reveal hadn't completed when Save was clicked, omit
      // archiveLink so the server keeps the existing ciphertext.
      const hadArchive = !!(cert.archiveLinkSet || cert.archiveLink);
      if (hadArchive && !editForm.archiveLink) delete payload.archiveLink;
      await updateCertificate(cert.taskId, cert.id, payload);
      setEditingId(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving');
    } finally {
      setSaving(false);
    }
  };

  // ── Inline edit ────────────────────────────────────────────────────────────

  const startInline = async (cert: Certificate, field: InlineField) => {
    if (!canEditCert(cert) || editingId === cert.id) return;
    let value = '';
    if (field === 'udin') value = cert.udin || '';
    else if (field === 'udinDate') value = cert.udinDate ? cert.udinDate.slice(0, 10) : '';
    else if (field === 'archiveLink') {
      // Reveal-and-audit just-in-time so the user can edit the existing value.
      if (cert.archiveLinkSet || cert.archiveLink) {
        try {
          const res = await revealCertificateArchiveLink(cert.id);
          value = res.data?.url || '';
        } catch { value = ''; }
      }
    }
    setInlineEdit({ certId: cert.id, field, value });
  };

  const saveInline = async (cert: Certificate) => {
    if (!inlineEdit || inlineEdit.certId !== cert.id || inlineSaving) return;
    setInlineSaving(true);
    try {
      // Only send the field that was edited. The server treats `undefined`
      // fields as "leave existing value alone" — crucial here because
      // cert.archiveLink in list responses is now always masked-empty, so
      // including it would wipe the stored ciphertext.
      const payload: any = {
        name: cert.name,
        description: cert.description || null,
        assignedToId: cert.assignedTo ? String(cert.assignedTo.id) : null,
        dueDate: cert.dueDate ? cert.dueDate.slice(0, 10) : null,
      };
      if (inlineEdit.field === 'udin') payload.udin = inlineEdit.value || null;
      else payload.udin = cert.udin || null;
      if (inlineEdit.field === 'udinDate') payload.udinDate = inlineEdit.value || null;
      else payload.udinDate = cert.udinDate ? cert.udinDate.slice(0, 10) : null;
      if (inlineEdit.field === 'archiveLink') payload.archiveLink = inlineEdit.value || null;
      // else: omit — server keeps existing ciphertext
      await updateCertificate(cert.taskId, cert.id, payload);
      setInlineEdit(null);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving');
    } finally {
      setInlineSaving(false);
    }
  };

  const cancelInline = () => setInlineEdit(null);

  // ── File actions ───────────────────────────────────────────────────────────

  const handleDelete = async (cert: Certificate) => {
    if (!window.confirm(`Delete certificate ${cert.certificateNumber}?`)) return;
    try {
      await deleteCertificate(cert.taskId, cert.id);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting');
    }
  };

  const handleFileUpload = async (cert: Certificate, file: File) => {
    setUploadingId(cert.id);
    try {
      await uploadCertificateFile(cert.id, file);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteFile = async (cert: Certificate) => {
    if (!window.confirm('Remove uploaded file?')) return;
    try {
      await deleteCertificateFile(cert.id);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error removing file');
    }
  };

  const isEditing = (id: number) => editingId === id;

  const taskOptions: FilterOption[] = useMemo(() =>
    tasks
      .filter((t: any) => !filterClientId || String(t.clientId) === filterClientId)
      .map((t) => ({ value: String(t.id), label: t.taskId })),
    [tasks, filterClientId]
  );

  const clientOptions: FilterOption[] = useMemo(() =>
    clients.map((c) => ({ value: String(c.id), label: c.clientName })),
    [clients]
  );

  // ── Inline-editable cell renderer ──────────────────────────────────────────

  const inlineCell = (cert: Certificate, field: InlineField) => {
    const active = inlineEdit?.certId === cert.id && inlineEdit.field === field;
    const canEdit = canEditCert(cert);

    if (active) {
      const commonProps = {
        autoFocus: true,
        value: inlineEdit!.value,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          setInlineEdit({ ...inlineEdit!, value: e.target.value }),
        onBlur: () => saveInline(cert),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter') saveInline(cert);
          if (e.key === 'Escape') cancelInline();
        },
      };
      if (field === 'udinDate') {
        return <input type="date" className="input-field text-xs py-0.5 w-32" {...commonProps} />;
      }
      return (
        <input
          className={`input-field text-xs py-0.5 ${field === 'udin' ? 'w-44 font-mono' : 'w-48'}`}
          placeholder={field === 'udin' ? '18-digit UDIN' : 'https://…'}
          {...commonProps}
        />
      );
    }

    if (field === 'udin') {
      return cert.udin ? (
        <span
          className={`font-mono text-xs bg-green-50 text-green-800 px-1.5 py-0.5 rounded border border-green-200 ${canEdit ? 'cursor-pointer hover:bg-green-100' : ''}`}
          onClick={() => canEdit && startInline(cert, field)}
          title={canEdit ? 'Click to edit' : undefined}
        >{cert.udin}</span>
      ) : canEdit ? (
        <span className="text-xs text-gray-400 cursor-pointer hover:text-blue-600 hover:underline"
          onClick={() => startInline(cert, field)}>+ Add UDIN</span>
      ) : <span className="text-gray-300 text-xs">—</span>;
    }

    if (field === 'udinDate') {
      return cert.udinDate ? (
        <span
          className={`text-xs text-gray-600 ${canEdit ? 'cursor-pointer hover:text-blue-600 hover:underline' : ''}`}
          onClick={() => canEdit && startInline(cert, field)}
          title={canEdit ? 'Click to edit' : undefined}
        >{fmtDate(cert.udinDate)}</span>
      ) : canEdit ? (
        <span className="text-xs text-gray-400 cursor-pointer hover:text-blue-600 hover:underline"
          onClick={() => startInline(cert, field)}>+ Add date</span>
      ) : <span className="text-gray-300 text-xs">—</span>;
    }

    // archiveLink — URL never appears in the rendered HTML. "Open" fetches
    // it from the server (audited); "✎" reveals only for the inline editor.
    const hasArchive = !!(cert.archiveLinkSet || cert.archiveLink);
    const openArchive = async () => {
      // Open blank window synchronously so the popup blocker treats this as
      // a user gesture; set its URL once the reveal returns.
      const w = window.open('', '_blank', 'noopener,noreferrer');
      try {
        const res = await revealCertificateArchiveLink(cert.id);
        const url = res.data?.url || '';
        if (!url) {
          if (w) w.close();
          showToast('Archive link is empty', 'error');
          return;
        }
        // Browsers won't navigate to UNC / file:// paths from web context —
        // copy to clipboard so the user can paste into Explorer.
        if (!/^https?:\/\//i.test(url)) {
          if (w) w.close();
          try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
          showToast(`✓ Archive path copied — paste into Windows Explorer\n${url}`, 'success', 6000);
          return;
        }
        if (w) w.location.href = url;
        else window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err: any) {
        if (w) w.close();
        showToast(err?.response?.data?.message || 'Failed to open archive link', 'error');
      }
    };
    return hasArchive ? (
      <div className="flex items-center gap-1">
        <button type="button" onClick={openArchive}
          className="text-xs text-indigo-600 hover:underline" title="Open archive (logged)">🔗 Open</button>
        {canEdit && (
          <button className="text-xs text-gray-400 hover:text-blue-600 leading-none"
            onClick={() => startInline(cert, field)} title="Edit link">✎</button>
        )}
      </div>
    ) : canEdit ? (
      <span className="text-xs text-gray-400 cursor-pointer hover:text-blue-600 hover:underline"
        onClick={() => startInline(cert, field)}>+ Add link</span>
    ) : <span className="text-gray-300 text-xs">—</span>;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-full">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input className="input-field w-56 text-sm" placeholder="Search name / UDIN / number…"
          value={search} onChange={(e) => setSearch(e.target.value)} />

        {/* Open / Closed / All toggle */}
        <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
          {(['open', 'closed', 'all'] as const).map((s) => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                filterStatus === s ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Billed / Unbilled / All toggle */}
        <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
          {(['all', 'billed', 'unbilled'] as const).map((s) => (
            <button key={s} onClick={() => setFilterBilling(s)}
              className={`text-xs px-3 py-1 rounded-md font-medium transition-colors ${
                filterBilling === s ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {s === 'all' ? 'All Billing' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <button className="btn-secondary text-sm" onClick={load}>Refresh</button>
        <span className="text-xs text-gray-400 ml-auto">
          {displayedCerts.length}{(filterStatus !== 'all' || filterBilling !== 'all') ? ` / ${certs.length}` : ''} certificate{certs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Bulk action bar — visible when ≥1 cert selected */}
      {selectedIds.size > 0 && (
        <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-emerald-800">
            {selectedIds.size} certificate{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          {bulkClientMixed && (
            <span className="text-xs text-red-600 font-medium">
              ⚠ Multiple clients — pick certificates of a single client to invoice together
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button
              onClick={createMultiCertInvoice}
              disabled={selectedIds.size < 2 || bulkClientMixed}
              className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
              📄 Create Multi-Certificate Invoice
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              className="text-xs px-3 py-1.5 rounded bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-gray-400">Loading…</div>
        ) : displayedCerts.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-gray-400">No certificates found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-header w-8">
                  {selectableCerts.length > 0 && (
                    <input type="checkbox" className="w-4 h-4 rounded"
                      checked={selectableCerts.length > 0 && selectableCerts.every((c) => selectedIds.has(c.id))}
                      onChange={toggleSelectAllVisible}
                      title="Select all unbilled certificates on this view" />
                  )}
                </th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('certificateNumber')}><span className="font-semibold">Cert No.</span> <SI f="certificateNumber" /></th>
                <th className="table-header">
                  <ColumnFilter label="Task" options={taskOptions}
                    value={filterTaskId} onChange={(v) => setFilterTaskId(v)}
                    sortable onSort={() => handleSort('task')} sortIcon={<SI f="task" />} />
                </th>
                <th className="table-header">
                  <ColumnFilter label="Client" options={clientOptions}
                    value={filterClientId} onChange={(v) => { setFilterClientId(v); setFilterTaskId(''); }}
                    sortable onSort={() => handleSort('client')} sortIcon={<SI f="client" />} />
                </th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('name')}><span className="font-semibold">Name</span> <SI f="name" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('assignedTo')}><span className="font-semibold">Assigned To</span> <SI f="assignedTo" /></th>
                <th className="bg-gray-50 px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 w-20 whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('createdAt')}>Created <SI f="createdAt" /></th>
                <th className="bg-gray-50 px-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 w-20 cursor-pointer select-none" onClick={() => handleSort('dueDate')}>Due <SI f="dueDate" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('udin')}><span className="font-semibold">UDIN</span> <SI f="udin" /></th>
                <th className="table-header whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('udinDate')}><span className="font-semibold">UDIN Date</span> <SI f="udinDate" /></th>
                <th className="table-header cursor-pointer select-none" onClick={() => handleSort('status')}><span className="font-semibold">Archive</span> <SI f="status" /></th>
                <th className="table-header"><span className="font-semibold">File</span></th>
                <th className="table-header w-10"></th>
                <th className="table-header"><span className="font-semibold">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {displayedCerts.map((cert) => {
                const closed = isClosed(cert);
                return (
                  <tr key={cert.id}
                    className={`border-b border-gray-50 hover:bg-gray-50 ${isEditing(cert.id) ? 'bg-blue-50' : closed ? 'bg-green-50/30' : ''} ${selectedIds.has(cert.id) ? 'bg-emerald-50/60' : ''}`}>

                    {/* Bulk select */}
                    <td className="table-cell w-8">
                      {!cert.invoice && canEditCert(cert) && (
                        <input type="checkbox" className="w-4 h-4 rounded"
                          checked={selectedIds.has(cert.id)}
                          onChange={() => toggleSelect(cert.id)}
                          disabled={!!cert.invoice}
                          title={bulkClientLocked && cert.task?.client?.id !== bulkClientLocked
                            ? 'Different client than already-selected certs'
                            : ''} />
                      )}
                    </td>

                    {/* Cert number */}
                    <td className="table-cell whitespace-nowrap">
                      <span className="font-mono text-xs text-blue-700 font-semibold">{cert.certificateNumber}</span>
                      {closed && <span className="ml-1.5 text-xs text-green-600 font-semibold" title="All fields complete">✓</span>}
                    </td>

                    {/* Task */}
                    <td className="table-cell">
                      <button className="text-blue-600 hover:underline text-xs font-medium"
                        onClick={() => navigate('/tasks')} title={cert.task.taskName}>
                        {cert.task.taskId}
                      </button>
                      <div className="text-xs text-gray-400 truncate max-w-[120px]" title={cert.task.taskName}>{cert.task.taskName}</div>
                    </td>

                    {/* Client */}
                    <td className="table-cell text-xs text-gray-600 whitespace-nowrap" title={cert.task.client.clientName}>
                      {cert.task.client.clientName}
                    </td>

                    {/* Name + description */}
                    <td className="table-cell font-medium">
                      {isEditing(cert.id) ? (
                        <div className="space-y-1">
                          <input className="input-field text-sm py-1" value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                          <textarea className="input-field text-xs py-1" rows={2} placeholder="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                        </div>
                      ) : (
                        <>
                          <div title={cert.name}>{cert.name}</div>
                          {cert.description && (
                            <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]" title={cert.description}>{cert.description}</div>
                          )}
                        </>
                      )}
                    </td>

                    {/* Assigned To */}
                    <td className="table-cell text-xs">
                      {isEditing(cert.id) ? (
                        <select className="input-field text-sm py-1"
                          value={editForm.assignedToId}
                          onChange={(e) => setEditForm({ ...editForm, assignedToId: e.target.value })}>
                          <option value="">— None —</option>
                          {staff.filter((s) => s.isActive !== false).map((s) =>
                            <option key={s.id} value={s.id}>{s.staffName}</option>
                          )}
                        </select>
                      ) : cert.assignedTo ? (
                        <span className="text-gray-700">{cert.assignedTo.staffName}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* Created (compact) */}
                    <td className="px-2 py-3 text-xs text-gray-500 w-20 border-b border-gray-50 whitespace-nowrap">
                      {fmtDate(cert.createdAt)}
                    </td>

                    {/* Due Date (compact; editable in full edit mode) */}
                    <td className="px-2 py-3 text-xs w-20 border-b border-gray-50 whitespace-nowrap">
                      {isEditing(cert.id) ? (
                        <input type="date" className="input-field text-xs py-0.5 w-28"
                          value={editForm.dueDate}
                          onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })} />
                      ) : cert.dueDate ? (
                        <span className="text-gray-600">{fmtDate(cert.dueDate)}</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>

                    {/* UDIN (inline editable) */}
                    <td className="table-cell">
                      {isEditing(cert.id) ? (
                        <input className="input-field text-xs py-0.5 w-44 font-mono" placeholder="18-digit UDIN"
                          value={editForm.udin}
                          onChange={(e) => setEditForm({ ...editForm, udin: e.target.value })} />
                      ) : inlineCell(cert, 'udin')}
                    </td>

                    {/* UDIN Date (inline editable) */}
                    <td className="table-cell">
                      {isEditing(cert.id) ? (
                        <input type="date" className="input-field text-xs py-0.5"
                          value={editForm.udinDate}
                          onChange={(e) => setEditForm({ ...editForm, udinDate: e.target.value })} />
                      ) : inlineCell(cert, 'udinDate')}
                    </td>

                    {/* Archive Link (inline editable) */}
                    <td className="table-cell">
                      {isEditing(cert.id) ? (
                        <input className="input-field text-xs py-0.5 w-48" placeholder="https://…"
                          value={editForm.archiveLink}
                          onChange={(e) => setEditForm({ ...editForm, archiveLink: e.target.value })} />
                      ) : inlineCell(cert, 'archiveLink')}
                    </td>

                    {/* Certificate File */}
                    <td className="table-cell">
                      {cert.fileName ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <a href={uploadsUrl(cert.fileName)} target="_blank" rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline" title={cert.originalName}>
                            📄 {cert.originalName && cert.originalName.length > 14
                              ? cert.originalName.slice(0, 12) + '…'
                              : cert.originalName}
                          </a>
                          {canEditCert(cert) && (
                            <button className="text-red-400 hover:text-red-600 text-xs"
                              onClick={() => handleDeleteFile(cert)} title="Remove file">✕</button>
                          )}
                        </div>
                      ) : canEditCert(cert) ? (
                        <label className={`cursor-pointer text-xs text-indigo-600 hover:text-indigo-800 font-medium ${uploadingId === cert.id ? 'opacity-50 pointer-events-none' : ''}`}>
                          {uploadingId === cert.id ? 'Uploading…' : '+ Upload'}
                          <input type="file" className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleFileUpload(cert, f);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      ) : null}
                    </td>

                    {/* Status dots */}
                    <td className="table-cell w-10">
                      <CertStatusDot closed={closed} invoiced={!!cert.invoice} />
                    </td>

                    {/* Actions: Edit + Del + Invoice in one column */}
                    <td className="table-cell">
                      <div className="flex gap-2 items-center flex-wrap">
                        {canEditCert(cert) && !isEditing(cert.id) && (
                          <button className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => startEdit(cert)}>Edit</button>
                        )}
                        {isEditing(cert.id) && (
                          <>
                            <button className="text-xs text-green-700 hover:text-green-900 font-medium"
                              onClick={() => saveEdit(cert)} disabled={saving}>
                              {saving ? 'Saving…' : 'Save'}
                            </button>
                            <button className="text-xs text-gray-500 hover:text-gray-700"
                              onClick={cancelEdit}>Cancel</button>
                          </>
                        )}
                        {cert.invoice ? (
                          <button className="text-xs text-blue-700 hover:underline font-medium"
                            onClick={() => navigate('/invoices')} title={cert.invoice.invoiceNumber}>
                            {cert.invoice.invoiceNumber}
                          </button>
                        ) : canEditCert(cert) && !isEditing(cert.id) ? (
                          <button className="text-xs text-emerald-600 hover:text-emerald-800 font-medium whitespace-nowrap"
                            onClick={() => navigate(`/invoices?certificateIds=${cert.id}`)}>
                            + Invoice
                          </button>
                        ) : null}
                        {(isAdmin || isPartner) && !isEditing(cert.id) && (
                          <button className="text-xs text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(cert)}>Del</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(isAdmin || isHR) && (
        <p className="text-xs text-gray-400 mt-3">Create certificates from the task detail panel</p>
      )}
    </div>
  );
};

export default Certificates;
